import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, AlertCircle } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import StickyActions from '../../../components/StickyActions';
import SelectorCuenta from '../../../components/Tesoreria/SelectorCuenta';
import { useMedioPago, useMedioPagoMutations } from '../../../graphql/tesoreria';

export default function EditarMedioPago() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    nombre: '',
    tipo: 'EFECTIVO',
    cuentaBancariaId: '',
    habilitado: true,
    notas: '',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [result] = useMedioPago(id);
  const { createMedio, updateMedio } = useMedioPagoMutations();
  const medio = result.data?.medioPago;

  useEffect(() => {
    if (medio && isEditing) {
      setFormData({
        nombre: medio.nombre || '',
        tipo: medio.tipo || 'EFECTIVO',
        cuentaBancariaId: medio.cuentaBancariaId || '',
        habilitado: medio.habilitado !== undefined ? medio.habilitado : true,
        notas: medio.notas || '',
      });
    }
  }, [medio, isEditing]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSubmit(e);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [formData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es obligatorio';
    if (!formData.tipo) newErrors.tipo = 'El tipo es obligatorio';
    
    // Validar cuenta bancaria para ciertos tipos
    if (['TRANSFERENCIA', 'TARJETA', 'MONI'].includes(formData.tipo) && !formData.cuentaBancariaId) {
      newErrors.cuentaBancariaId = 'Se recomienda asociar una cuenta bancaria para este tipo de medio';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await updateMedio({ id, input: formData });
        alert('Medio de pago actualizado correctamente');
      } else {
        await createMedio({ input: formData });
        alert('Medio de pago creado correctamente');
      }
      navigate('/Tesoreria/MediosPago');
    } catch (error) {
      alert(`Error al ${isEditing ? 'actualizar' : 'crear'} el medio de pago`);
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[rgba(var(--bg))] pb-24">
      <PageHeader
        title={isEditing ? 'Editar Medio de Pago' : 'Nuevo Medio de Pago'}
        subtitle={isEditing ? 'Modificar información del medio' : 'Registrar un nuevo medio de pago'}
      />

      <section className="max-w-[1400px] mx-auto px-4 py-6 grid grid-cols-12 gap-4">
        <div className="col-span-12 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)] p-6">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 md:col-span-6">
                <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => handleChange('nombre', e.target.value)}
                  className={`w-full px-4 py-2.5 rounded-xl border bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] ${
                    errors.nombre ? 'border-red-500' : 'border-[rgb(var(--border))]'
                  }`}
                  placeholder="Ej: Efectivo"
                  aria-invalid={!!errors.nombre}
                />
                {errors.nombre && <p className="mt-1 text-sm text-red-500">{errors.nombre}</p>}
              </div>

              <div className="col-span-12 md:col-span-6">
                <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">
                  Tipo <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.tipo}
                  onChange={(e) => handleChange('tipo', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                >
                  <option value="EFECTIVO">Efectivo</option>
                  <option value="TRANSFERENCIA">Transferencia</option>
                  <option value="TARJETA">Tarjeta</option>
                  <option value="MONI">Moni</option>
                  <option value="CHEQUE">Cheque</option>
                  <option value="OTRO">Otro</option>
                </select>
              </div>

              <div className="col-span-12">
                <SelectorCuenta
                  value={formData.cuentaBancariaId}
                  onChange={(cuenta) => handleChange('cuentaBancariaId', cuenta?.id || '')}
                  label="Cuenta Bancaria Asociada"
                  placeholder="Seleccionar cuenta (opcional)"
                  error={errors.cuentaBancariaId}
                />
              </div>

              <div className="col-span-12">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.habilitado}
                    onChange={(e) => handleChange('habilitado', e.target.checked)}
                    className="w-4 h-4 rounded border-[rgb(var(--border))] text-[rgb(var(--brand))] focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  />
                  <span className="text-sm font-medium text-[rgb(var(--fg))]">Habilitado</span>
                </label>
              </div>

              <div className="col-span-12">
                <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">Notas</label>
                <textarea
                  value={formData.notas}
                  onChange={(e) => handleChange('notas', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] resize-none"
                  placeholder="Notas adicionales..."
                />
              </div>
            </div>
          </form>
        </div>
      </section>

      <StickyActions
        actions={[
          <button
            key="guardar"
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[rgb(var(--brand))] text-white hover:bg-[rgb(var(--brand-700))] disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          >
            <Save className="w-4 h-4" aria-hidden="true" />
            {saving ? 'Guardando...' : 'Guardar'}
          </button>,
          <button
            key="cancelar"
            onClick={() => navigate('/Tesoreria/MediosPago')}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl border border-[rgb(var(--border))] text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.3)] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          >
            <X className="w-4 h-4" aria-hidden="true" />
            Cancelar
          </button>,
        ]}
      />
    </div>
  );
}
